import { array, option, ord, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';

import { getProp } from '@mews/fp';
import { DateTimeService, DateTimeUtc, DateTimeUtils, WallClockDate, WallClockDateService } from '@mews/framework';
import { CalendarService, StructuredDate } from '@optimus-web/core';

import { isAppFetching } from '../../modules/app/selectors';
import { areAvailabilityBlocksFetching } from '../../modules/entities/availabilityBlocks/selectors';
import { getServiceAvailabilityData } from '../../modules/serviceAvailability/selectors';
import { isOccupiedDate } from '../../views/dates/components/DateRange/utils';

import { getSelectedCityEnterpriseTimezone } from './selectedCityEnterprises';

export const getMinimalStartDate = createSelector(
    getSelectedCityEnterpriseTimezone,
    getProp<DateTimeUtc>()('nowUtc'),
    (timezone, nowUtc) => {
        const { value } = DateTimeService.toDate(DateTimeService.fromUtc(nowUtc, timezone));
        return WallClockDateService.parse(value);
    }
);

const byUtcString = pipe(
    string.Ord,
    ord.contramap((utc: string) => utc)
);

export const lastFetchedDateInServiceAvailability = createSelector(
    getServiceAvailabilityData,
    getSelectedCityEnterpriseTimezone,
    (data, timezone) =>
        pipe(
            data,
            record.toArray,
            array.map(([_, { timeUnitsAvailability }]) =>
                pipe(timeUnitsAvailability, record.keys, array.sortBy([byUtcString]), array.last)
            ),
            array.compact,
            array.sortBy([byUtcString]),
            array.last,
            option.match(
                () => undefined,
                (date) => DateTimeUtils.dateTimeUtcStringToWallClockDate(date, timezone)
            )
        )
);

interface GetValidStartDateEndDateOptions {
    calendarService: CalendarService;
    timezone: string;
    occupiedStartDates: Set<string>;
    startDate: WallClockDate;
    daysInterval?: number;
    maximalEndDate: WallClockDate;
    isDayService: boolean;
    isHourlyService: boolean;
    lastDateInServiceAvailabilityData?: WallClockDate;
}
interface ValidStartDateEndDate {
    nonDisabledStartDate: WallClockDate;
    nonDisabledEndDate: WallClockDate;
}

export const getValidStartDateEndDate = ({
    calendarService,
    timezone,
    occupiedStartDates,
    startDate,
    daysInterval = 1,
    maximalEndDate,
    isDayService,
    isHourlyService,
    lastDateInServiceAvailabilityData,
}: GetValidStartDateEndDateOptions): ValidStartDateEndDate | null => {
    const isStructuredDateOccupied = (date: StructuredDate) =>
        isOccupiedDate({ calendarService, timezone, occupiedDates: occupiedStartDates, date, isHourlyService });
    const structuredMaximalEndDate = DateTimeUtils.wallClockDateToStructuredDate(maximalEndDate, calendarService);
    let structuredStartDate = DateTimeUtils.wallClockDateToStructuredDate(startDate, calendarService);

    const structeredFinalDateInData =
        lastDateInServiceAvailabilityData != null
            ? DateTimeUtils.wallClockDateToStructuredDate(lastDateInServiceAvailabilityData, calendarService)
            : null;

    while (isStructuredDateOccupied(structuredStartDate)) {
        structuredStartDate = calendarService.addDays(structuredStartDate, 1);

        /**
         * In order to find the correct startDate we need to check if today's date is occupied or not.
         * If we dont find atleast one occupied date, we return null which will be handled by the caller.
         */
        const hasNotFoundOccupiedDate = calendarService.isDateEqual(structuredStartDate, structuredMaximalEndDate);

        /**
         * if we do not yet have the data for the checked date, we return null as we do above
         */
        const hasNoDataForCheckedDate =
            isHourlyService &&
            structeredFinalDateInData != null &&
            calendarService.isDateGreater(structeredFinalDateInData, structuredStartDate);

        if (hasNotFoundOccupiedDate || hasNoDataForCheckedDate) {
            return null;
        }
    }

    /**
     * At this point, we know that there is at least one non-occupied date, and we store it in structuredStartDate.
     * We are now finding the endDate we'll use.
     * We know we'll find one because there's at least one non-occupied date before the endDate, the original structuredStartDate, and our algorithm walks back from the initial end date.
     * The question, if we can actually use it, is answered in the next block.
     */

    let structuredEndDate = null;
    if (
        calendarService.isDateGreater(
            calendarService.addDays(structuredStartDate, daysInterval),
            structuredMaximalEndDate
        )
    ) {
        structuredEndDate = structuredMaximalEndDate;
    } else {
        structuredEndDate = calendarService.addDays(structuredStartDate, daysInterval);
    }

    /**
     * At this point we need to decide what will be the endDate which we'll use when we found only one day.
     * If We are staying overnight (night service), we will use the endDate as the startDate + 1 day.
     * */
    if (calendarService.isDateEqual(structuredEndDate, structuredStartDate) && !isDayService && !isHourlyService) {
        return {
            nonDisabledStartDate: WallClockDateService.parse(calendarService.toISOString(structuredStartDate)),
            nonDisabledEndDate: WallClockDateService.parse(
                calendarService.toISOString(calendarService.addDays(structuredStartDate, 1))
            ),
        };
    }

    if (calendarService.isDateLess(structuredEndDate, structuredStartDate) && isHourlyService) {
        const startAndEndDate = WallClockDateService.parse(calendarService.toISOString(structuredStartDate));
        return {
            nonDisabledStartDate: startAndEndDate,
            nonDisabledEndDate: startAndEndDate,
        };
    }

    return {
        nonDisabledStartDate: WallClockDateService.parse(calendarService.toISOString(structuredStartDate)),
        nonDisabledEndDate: WallClockDateService.parse(calendarService.toISOString(structuredEndDate)),
    };
};

export const getMinimalEndDate = createSelector(getMinimalStartDate, (minimalStartDate) =>
    WallClockDateService.addDays(minimalStartDate, 1)
);

export const isDatesViewFetching = createSelector(
    isAppFetching,
    areAvailabilityBlocksFetching,
    (appFetching, availabilityBlocksFetching) => appFetching || availabilityBlocksFetching
);
